// src/components/inventory/CategoryFormModal.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Duplicate names are caught before saving (case-insensitive, `existingNames`); the name is
//     trimmed; length limits; the server message is shown when a save fails.
//   • FlowSense AI "Suggest description" (the same Ollama cataloguing endpoint the item form
//     uses); a clear message when the AI engine is offline.
//   • Only name + description are sent (the edit form used to post the whole row back).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FormErrorMessage, FormHelperText, HStack, Icon } from '@chakra-ui/react'; // --- UPDATE
import { MdAutoAwesome } from 'react-icons/md'; // --- UPDATE
import inventoryCategoryService from '../../services/inventoryCategoryService';
import inventoryService from '../../services/inventoryService'; // --- UPDATE

const CategoryFormModal = ({ isOpen, onClose, category, onSave, existingNames = [] }) => { // --- UPDATE: + existingNames
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false); // --- UPDATE
  const toast = useToast();
  const modalBg = useColorModeValue("white", "navy.800");

  useEffect(() => {
    if (category) {
      setFormData({ name: category.name || '', description: category.description || '' }); // --- UPDATE: only the editable fields
    } else {
      setFormData({ name: '', description: '' });
    }
  }, [category, isOpen]);

  // --- UPDATE: validation
  const trimmed = String(formData.name || '').trim();
  const duplicate = trimmed && existingNames.some((n) => String(n || '').trim().toLowerCase() === trimmed.toLowerCase());
  const nameError = !trimmed ? null : duplicate ? 'A category with this name already exists.' : trimmed.length > 100 ? 'Use 100 characters or fewer.' : null;
  // --- END UPDATE

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- UPDATE: FlowSense AI description
  const suggest = async () => {
    if (!trimmed) { toast({ title: 'Enter a name first', status: 'warning', duration: 2000 }); return; }
    setAiBusy(true);
    try {
      const r = await inventoryService.generateItemDetails(`${trimmed} (inventory category)`, 'Inventory category for the Ministry of Works stores');
      const d = r?.data?.description || r?.description;
      if (!d) throw new Error('empty');
      setFormData((f) => ({ ...f, description: String(d).slice(0, 500) }));
    } catch (_) {
      toast({ title: 'FlowSense AI is offline', description: 'Write the description yourself or start the local AI engine (Ollama).', status: 'info' });
    } finally { setAiBusy(false); }
  };
  // --- END UPDATE

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmed || nameError) return; // --- UPDATE
    setIsSubmitting(true);
    const body = { name: trimmed, description: String(formData.description || '').trim() }; // --- UPDATE
    try {
      if (category) {
        await inventoryCategoryService.updateCategory(category.id, body);
      } else {
        await inventoryCategoryService.createCategory(body);
      }
      toast({
        title: `Category ${category ? 'updated' : 'created'}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onSave();
      onClose();
    } catch (error) {
      toast({
        title: 'An error occurred.',
        description: error?.message || `Failed to save category.`, // --- UPDATE: server message
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg}>
        <ModalHeader>{category ? 'Edit Category' : 'Add New Category'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel>Category Name</FormLabel>
              <Input name="name" value={formData.name} onChange={handleChange} maxLength={120} placeholder="e.g. Valves & fittings" />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}{/* --- UPDATE */}
            </FormControl>
            <FormControl>
              {/* --- UPDATE: AI suggestion */}
              <HStack justify="space-between" mb={1}>
                <FormLabel mb={0}>Description</FormLabel>
                <Button size="xs" variant="outline" colorScheme="purple" leftIcon={<Icon as={MdAutoAwesome} />} onClick={suggest} isLoading={aiBusy} loadingText="Thinking…">Suggest with FlowSense AI</Button>
              </HStack>
              <Textarea name="description" value={formData.description} onChange={handleChange} maxLength={500} />
              <FormHelperText fontSize="xs">{String(formData.description || '').length}/500</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" type="submit" isLoading={isSubmitting} isDisabled={!trimmed || !!nameError}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CategoryFormModal;
